import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RouteContext = createContext();

export function RouteProvider({ children }) {

  const navigate = useNavigate();

  const [customer, setCustomerRaw] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('orderNumber') && urlParams.get('email') && urlParams.get('autoSearch') === 'true') {
        return null;
      }
      const session = JSON.parse(localStorage.getItem('brella_customer'));
      if (!session || !session.user || Date.now() > session.expiresAt) {
        localStorage.removeItem('brella_customer');
        return null;
      }
      return session.user;
    } catch {
      return null;
    }
  });

  const [orderDetails, setOrderDetailsRaw] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('orderNumber') && urlParams.get('email') && urlParams.get('autoSearch') === 'true') {
        return null;
      }
      const stored = localStorage.getItem('brella_order');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [trackingInfo, setTrackingInfoRaw] = useState(() => {
    try {
      const stored = localStorage.getItem('brella_tracking_info');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // ✅ Wrapped setters that persist to localStorage
  const setCustomer = (customerData) => {
    const session = {
      user: customerData,
      expiresAt: Date.now() + 12 * 60 * 60 * 1000, // 12 hrs
    };
    localStorage.setItem('brella_customer', JSON.stringify(session));
    setCustomerRaw(customerData);
  };

  const [shopifyProducts, setShopifyProductsRaw] = useState(() => {
    try {
      const stored = localStorage.getItem('brella_products');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setShopifyProducts = (products) => {
    localStorage.setItem('brella_products', JSON.stringify(products));
    setShopifyProductsRaw(products);
  };

  const setOrderDetails = (orderData) => {
    localStorage.setItem('brella_order', JSON.stringify(orderData));
    setOrderDetailsRaw(orderData);
  };

  const setTrackingInfo = (trackingData) => {
    localStorage.setItem('brella_tracking_info', JSON.stringify(trackingData));
    setTrackingInfoRaw(trackingData);
  };

  const clearOrderDetails = () => {
    localStorage.removeItem('brella_order');
    setOrderDetailsRaw(null);
  };

  const logout = () => {
    localStorage.removeItem('brella_customer');
    localStorage.removeItem('brella_order');
    localStorage.removeItem('brella_tracking_info');
    localStorage.removeItem('brella_products');
    setShopifyProductsRaw(null);
    setCustomerRaw(null);
    setOrderDetailsRaw(null);
    setTrackingInfoRaw(null);
    const currentPath = window.location.pathname;
    if (currentPath.endsWith('/returns')) {
      navigate('/returns', { replace: true }); // stay on /returns
    } else if (currentPath.endsWith('/claims')) {
      navigate('/claims', { replace: true }); // redirect to home
    } else {
      navigate('/', { replace: true }); // redirect to home
    }
  };

  const value = {
    customer,
    setCustomer,
    orderDetails,
    setOrderDetails,
    trackingInfo,
    setTrackingInfo,
    shopifyProducts,
    setShopifyProducts,
    clearOrderDetails,
    logout,
  };

  return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
}

export function useRouteContext() {
  return useContext(RouteContext);
}
